import { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "tss-react/mui";
import {
  MiBadge,
  MiExpandableBox,
  MiIcon,
  MiLoader,
  MiPopper,
} from "@miplatform/components";
import {
  ATTEMPT_DERIVED_STATUS_COLORS_MAP,
  ATTEMPT_DERIVED_STATUS_LABEL,
  ATTEMPT_STATUSES,
  DATE_TIME_FORMATS,
  EMAIL_TEMPLATE_TYPE_TO_LABEL_MAP,
  EMAIL_TYPE,
  ONE_HOUR,
  TIME_FORMAT,
} from "@miplatform/constants";
import { EmailTemplateType } from "@miplatform/types";
import {
  formatDate,
  getContrastColor,
  getDateWithAddedTime,
  getDurationInHours,
  getFormattedDuration,
  toFormattedDate,
  toHour,
} from "@miplatform/utils";
import { mdiLockOutline } from "@mdi/js";
import {
  Box,
  ClickAwayListener,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { MiAttemptNumberBadge } from "../../../../components/MiAttemptNumberBadge/MiAttemptNumberBadge";
import { MiEmailInfoCard } from "../../../../components/MiEmailInfoCard/MiEmailInfoCard";
import { IssueCard } from "../CloseoutDrawer/IssueCard/IssueCard";
import { getStatusStyle } from "./taskOverviewUtil";
import { useTaskOverviewViewModel } from "./useTaskOverviewViewModel";

const LOCK_TYPE_LABEL_MAP = {
  User: "Technician",
  Time: "Time of Day",
  Date: "Date",
};
export function TaskOverview({
  anchorEl = null,
  closePopper = () => null,
  delayedTime = 0,
  isDelayed = false,
  isEarly = false,
  isInBucket = false,
  openDetails = () => null,
  attempt = {},
  issues = [],
  onEmailDrawerOpen = () => null,
  onEmailPreviewModalOpen = () => null,
  openHistoryAttempt = () => null,
  hideEmails = false,
  isNHC = false,
  startAutoCloseTimeout = () => null,
  stopAutoCloseTimeout = () => null,
}) {
  const {
    duration,
    exactExtendedTime = 0,
    home: { address = {} } = { address: {} },
  } = attempt;

  const isSubmitted = attempt.attemptStatus === ATTEMPT_STATUSES.SUBMITTED;
  const isInProgress = attempt.attemptStatus === ATTEMPT_STATUSES.STARTED;
  const isClosed = attempt.attemptStatus === ATTEMPT_STATUSES.CLOSED;
  const isClosingOut =
    attempt.attemptStatus === ATTEMPT_STATUSES.CLOSEOUT_IN_PROGRESS;
  const isDispatched =
    attempt.attemptStatus !== ATTEMPT_STATUSES.NEW &&
    attempt.attemptStatus !== ATTEMPT_STATUSES.PLANNED &&
    attempt.attemptStatus !== ATTEMPT_STATUSES.HOLD;

  const startTime = getFormattedDuration(toHour(attempt.displayStartTime), "");
  const isIssuesResolved = useMemo(
    () => issues.every((issue) => issue.issueResult),
    [issues]
  );
  const diffInDuration = attempt.endDate
    ? getDurationInHours(attempt.startDate, attempt.endDate) -
      getDurationInHours(attempt.scheduledStartDate, attempt.scheduledEndDate)
    : 0;

  const { isLoading, emailHistory, getEmailHistory } =
    useTaskOverviewViewModel(attempt);
  const orderColor = isNHC
    ? attempt.assignment.workOrder?.workOrderTemplateColor
    : attempt.assignment?.order?.orderTemplateColor;
  const { classes, theme } = useStyles({
    assignmentTemplateColor: orderColor,
    statusStyle: getStatusStyle(attempt.attemptStatus),
    isExtended: diffInDuration > 0 || exactExtendedTime > 0,
    colors: ATTEMPT_DERIVED_STATUS_COLORS_MAP[attempt?.derivedStatus] || {},
  });

  useEffect(() => {
    if (anchorEl && !isNHC) {
      getEmailHistory();
    }
  }, [anchorEl, isNHC]);

  const addressInfo = `${address.city}, ${address.regionCode} \u00A0 | \u00A0 ${
    attempt?.home?.builderName || ""
  }${
    attempt?.home?.builderShortName
      ? `  (${attempt?.home?.builderShortName}) \u00A0 | \u00A0`
      : ""
  }${address.postalCode}`;

  return (
    <MiPopper
      anchorEl={anchorEl}
      arrowColor={orderColor}
      placement={isInBucket && !isNHC ? "top" : "auto-end"}
      showArrow
    >
      <ClickAwayListener onClickAway={closePopper}>
        <Paper
          className={classes.popperContainer}
          id="popper-container"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onMouseEnter={stopAutoCloseTimeout}
          onMouseLeave={startAutoCloseTimeout}
        >
          <Box className={classes.popperHeader} data-testid="popper-header">
            <Typography className={classes.attemptTypeText}>
              {attempt.assignment.assignmentTemplateName}
            </Typography>
            <Box className={classes.attemptStatusContainer}>
              <MiBadge
                className={classes.attemptStatus}
                text={ATTEMPT_DERIVED_STATUS_LABEL[attempt.derivedStatus]}
                data-testid="attempt-status"
                fontSize={theme.fontSize.xsmall}
              />
            </Box>
          </Box>
          <Box className={classes.addressContainer}>
            <Box className={classes.popperAddress} onClick={openDetails}>
              <Tooltip title={address.addressLine1}>
                <Box height={11}>
                  <Typography className={classes.addressInfo1} noWrap>
                    {address.addressLine1}
                  </Typography>
                </Box>
              </Tooltip>
              <Tooltip title={addressInfo}>
                <Box className={classes.secondaryPopperAddress}>
                  <Typography className={classes.addressInfo2} noWrap>
                    {addressInfo}
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
            {attempt?.attemptNumber === "1" && (
              <Box className={classes.timeContainer}>
                <Typography className={classes.labelText}>
                  Requested Date
                </Typography>
                <Typography
                  className={classes.addressInfo1}
                  data-testid="requested-start-date"
                >
                  {formatDate(
                    attempt.order?.requestedStartDate,
                    DATE_TIME_FORMATS.FORMAT_MM_DD_YY
                  )}
                </Typography>
              </Box>
            )}
          </Box>
          <Box className={classes.popperTimeSection}>
            <Box className={classes.timeContainer}>
              <Typography className={classes.labelText}>
                Scheduled Time
              </Typography>
              {!isInBucket ? (
                <Box className={classes.flexRow}>
                  {(isEarly || isDelayed) && (
                    <Typography className={classes.scheduledStartDateText}>
                      {toFormattedDate(attempt.scheduledStartDate, TIME_FORMAT)}
                    </Typography>
                  )}
                  <Typography className={classes.timeText}>
                    {isDelayed
                      ? getDateWithAddedTime(
                          startTime,
                          { h: delayedTime },
                          TIME_FORMAT
                        )
                      : toFormattedDate(startTime, TIME_FORMAT)}
                  </Typography>
                </Box>
              ) : (
                <Typography className={classes.timeText}>TBD</Typography>
              )}
            </Box>
            <Box className={classes.timeContainer}>
              <Typography className={classes.labelText}>Duration</Typography>
              <Box className={classes.flexRow}>
                <Typography
                  className={classes.timeText}
                  data-testid="actual-timer"
                >
                  {getFormattedDuration(
                    isInBucket
                      ? attempt.expectedDuration / ONE_HOUR
                      : duration + exactExtendedTime
                  )}
                </Typography>
                <Typography
                  className={classes.diffTotalTimeText}
                  data-testid="diff-total-time"
                >
                  {isInProgress
                    ? `(${
                        exactExtendedTime > 0 ? "+" : "-"
                      }${getFormattedDuration(Math.abs(exactExtendedTime))})`
                    : null}
                  {diffInDuration
                    ? `(${diffInDuration > 0 ? "+" : "-"}${getFormattedDuration(
                        Math.abs(diffInDuration)
                      )})`
                    : null}
                </Typography>
              </Box>
            </Box>
            <Box
              className={classes.attemptNavigation}
              onClick={(e) => {
                e.stopPropagation();
                openHistoryAttempt(attempt);
              }}
            >
              <Typography className={classes.labelText}>
                Visit History
              </Typography>
              <MiAttemptNumberBadge
                attemptNumber={attempt.attemptNumber}
                className={classes.attemptNo}
              />
            </Box>
            <Box className={classes.timeContainer}>
              <Typography className={classes.labelText}>
                Time Preference
              </Typography>
              <Typography
                className={classes.timeText}
                data-testid="order-time-preference"
              >
                {attempt.orderTimePreferences || "N/A"}
              </Typography>
            </Box>
          </Box>
          {attempt.lockTypes?.length > 0 && (
            <Box className={classes.lockContainer}>
              <MiIcon
                path={mdiLockOutline}
                color={theme.palette.common.black}
                size={0.8}
              />
              <Box className={classes.lockInfo}>
                <Typography className={classes.lockInfoTitle}>
                  Order Locked
                </Typography>
                <Typography className={classes.lockInfoDescription}>
                  This order is locked to:{" "}
                  {attempt.lockTypes
                    .map(
                      (lockType) => LOCK_TYPE_LABEL_MAP[lockType] || lockType
                    )
                    .sort((a, b) => a.localeCompare(b))
                    .join(", ")}
                </Typography>
              </Box>
            </Box>
          )}
          {issues.length > 0 && (
            <MiExpandableBox
              defaultExpanded
              title="Order Issues"
              sx={{ pt: 1 }}
            >
              <Box sx={{ overflow: "auto", maxHeight: 250 }}>
                <Box display="flex" flexDirection="column" gap={1} pt={1}>
                  {issues.map((issue, index) => (
                    <IssueCard
                      key={issue.id}
                      issue={issue}
                      issueIndex={index + 1}
                      length={issues.length}
                    />
                  ))}
                </Box>
              </Box>
            </MiExpandableBox>
          )}
          {!isNHC && !hideEmails && (
            <MiExpandableBox defaultExpanded title="Dispatch Emails">
              <Box
                position="relative"
                display="flex"
                flexDirection="column"
                gap={1}
              >
                {isLoading && (
                  <MiLoader
                    isLoading
                    type="linear"
                    style={{ position: "absolute", top: 0 }}
                  />
                )}
                <MiEmailInfoCard
                  title={
                    EMAIL_TEMPLATE_TYPE_TO_LABEL_MAP[
                      EmailTemplateType.AppointmentConfirmation
                    ]
                  }
                  lastSent={
                    emailHistory[EmailTemplateType.AppointmentConfirmation]
                      ?.createdOn
                  }
                  isActionDisabled={
                    attempt.attemptStatus === ATTEMPT_STATUSES.NEW ||
                    attempt.attemptStatus === ATTEMPT_STATUSES.HOLD
                  }
                  isViewDisabled={
                    !emailHistory[EmailTemplateType.AppointmentConfirmation]
                  }
                  onViewClick={() =>
                    onEmailPreviewModalOpen(
                      emailHistory[EmailTemplateType.AppointmentConfirmation]
                    )
                  }
                  onActionClick={() =>
                    onEmailDrawerOpen({
                      attempt,
                      type: EMAIL_TYPE.SCHEDULE,
                    })
                  }
                />
                <MiEmailInfoCard
                  title={
                    EMAIL_TEMPLATE_TYPE_TO_LABEL_MAP[
                      EmailTemplateType.TechnicianEnRouteNotice
                    ]
                  }
                  lastSent={
                    emailHistory[EmailTemplateType.TechnicianEnRouteNotice]
                      ?.createdOn
                  }
                  isActionDisabled={!isDispatched}
                  isViewDisabled={
                    !emailHistory[EmailTemplateType.TechnicianEnRouteNotice]
                  }
                  onViewClick={() =>
                    onEmailPreviewModalOpen(
                      emailHistory[EmailTemplateType.TechnicianEnRouteNotice]
                    )
                  }
                  onActionClick={() =>
                    onEmailDrawerOpen({
                      attempt,
                      type: EMAIL_TYPE.TECHNICIAN_ENROUTE,
                    })
                  }
                />
                <MiEmailInfoCard
                  title={
                    EMAIL_TEMPLATE_TYPE_TO_LABEL_MAP[
                      EmailTemplateType.OrderAssignmentNotice
                    ]
                  }
                  lastSent={
                    emailHistory[EmailTemplateType.OrderAssignmentNotice]
                      ?.createdOn
                  }
                  isActionDisabled={
                    attempt.attemptStatus === ATTEMPT_STATUSES.NEW ||
                    attempt.attemptStatus === ATTEMPT_STATUSES.HOLD
                  }
                  isViewDisabled={
                    !emailHistory[EmailTemplateType.OrderAssignmentNotice]
                  }
                  onViewClick={() =>
                    onEmailPreviewModalOpen(
                      emailHistory[EmailTemplateType.OrderAssignmentNotice]
                    )
                  }
                  onActionClick={() =>
                    onEmailDrawerOpen({
                      attempt,
                      type: EMAIL_TYPE.ORDER_ASSIGNMENT,
                    })
                  }
                />
                <MiEmailInfoCard
                  title={
                    EMAIL_TEMPLATE_TYPE_TO_LABEL_MAP[
                      EmailTemplateType.CloseoutAndCompletionNotice
                    ]
                  }
                  lastSent={
                    emailHistory[EmailTemplateType.CloseoutAndCompletionNotice]
                      ?.createdOn
                  }
                  isActionDisabled={
                    (!isSubmitted && !isClosingOut && !isClosed) ||
                    !isIssuesResolved
                  }
                  isViewDisabled={
                    !emailHistory[EmailTemplateType.CloseoutAndCompletionNotice]
                  }
                  onViewClick={() =>
                    onEmailPreviewModalOpen(
                      emailHistory[
                        EmailTemplateType.CloseoutAndCompletionNotice
                      ]
                    )
                  }
                  onActionClick={() =>
                    onEmailDrawerOpen({
                      attempt,
                      type: EMAIL_TYPE.CLOSEOUT_AND_UPDATE,
                    })
                  }
                />
              </Box>
            </MiExpandableBox>
          )}
        </Paper>
      </ClickAwayListener>
    </MiPopper>
  );
}

const useStyles = makeStyles()(
  (theme, { colors, assignmentTemplateColor, statusStyle, isExtended }) => ({
    popperContainer: {
      width: 364,
      height: "100%",
      borderRadius: 6,
    },
    popperHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      boxSizing: "content-box",
      borderRadius: "6px 6px 0px 0px",
      padding: theme.spacing(1.5, 2),
      backgroundColor: assignmentTemplateColor || theme.palette.grey.darkAccent,
      color: getContrastColor(
        assignmentTemplateColor || theme.palette.grey.darkAccent
      ),
      height: 16,
    },
    issueNavigation: {
      display: "flex",
      justifyContent: "space-between",
    },
    attemptTypeText: {
      fontWeight: theme.fontWeight.regular,
      fontSize: theme.fontSize.medium,
    },
    addressContainer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      paddingRight: theme.spacing(2),
    },
    popperAddress: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      height: 43,
      padding: theme.spacing(2),
      cursor: "pointer",
    },
    secondaryPopperAddress: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyItems: "center",
      maxWidth: 250,
    },
    addressInfo1: {
      fontSize: theme.fontSize.medium,
      fontWeight: theme.fontWeight.bold,
      color: theme.palette.bluegrey.dark,
      maxWidth: 250,
    },
    addressInfo2: {
      fontWeight: theme.fontWeight.regular,
      fontSize: theme.fontSize.xsmall,
      color: theme.palette.bluegrey.dark,
    },
    popperTimeSection: {
      borderRadius: "0px 0px 1px 0px",
      padding: theme.spacing(2),
      marginTop: theme.spacing(1),
      display: "flex",
      justifyContent: "space-between",
      backgroundColor: statusStyle.backgroundColor,
    },
    timeContainer: {
      height: 36,
    },
    flexRow: {
      display: "flex",
      gap: 4,
    },
    labelText: {
      fontSize: theme.fontSize.small,
      fontWeight: theme.fontWeight.regular,
      color: theme.palette.grey.medium,
    },
    statusText: {
      fontWeight: theme.fontWeight.bold,
      fontSize: theme.fontSize.small,
      color: statusStyle.color,
    },
    scheduledStartDateText: {
      fontWeight: theme.fontWeight.regular,
      fontSize: theme.fontSize.small,
      color: theme.palette.red.main,
      textDecoration: "line-through",
    },
    timeText: {
      fontWeight: theme.fontWeight.bold,
      fontSize: theme.fontSize.small,
    },
    requestedDateText: {
      fontSize: theme.fontSize.medium,
      fontWeight: theme.fontWeight.bold,
      color: theme.palette.grey.medium,
    },
    diffTotalTimeText: {
      fontSize: theme.fontSize.small,
      color: isExtended ? theme.palette.red.main : theme.palette.green.main,
    },
    lockContainer: {
      display: "flex",
      alignItems: "center",
      padding: theme.spacing(1, 2),
      borderRadius: theme.spacing(1),
      backgroundColor: theme.palette.grey.accent,
      border: `1px solid ${theme.palette.grey.medium}`,
      gap: theme.spacing(2),
      margin: theme.spacing(2),
      marginBottom: 0,
    },
    lockInfo: {
      display: "flex",
      flexDirection: "column",
    },
    lockInfoTitle: {
      fontWeight: theme.fontWeight.semiBold,
      fontSize: theme.fontSize.small,
    },
    lockInfoDescription: {
      fontSize: theme.fontSize.small,
      color: theme.palette.grey.medium,
    },
    issueStatus: {
      width: 78,
      height: 25,
      borderRadius: 4,
      fontWeight: theme.fontWeight.regular,
      fontSize: theme.fontSize.medium,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      color: theme.palette.common.white,
    },
    issueTitle: {
      fontWeight: theme.fontWeight.bold,
      fontSize: theme.fontSize.medium,
    },
    issueDescription: {
      fontWeight: theme.fontWeight.regular,
      fontSize: theme.fontSize.small,
    },
    attemptNo: {
      ":hover": {
        backgroundColor: theme.palette.orange.medium,
        textDecoration: "underline",
        cursor: "pointer",
      },
    },
    attemptStatusContainer: {
      backgroundColor: theme.palette.common.white,
      borderRadius: theme.spacing(1),
    },
    attemptStatus: {
      color: `${colors.title || colors.main} !important`,
      whiteSpace: "nowrap",
      backgroundColor: colors.statusBackground,
      display: "flex",
      alignItems: "center",
      border: `1px solid ${colors.main}`,
      height: theme.spacing(4),
      borderRadius: theme.spacing(0.5),
    },
  })
);

TaskOverview.propTypes = {
  attempt: PropTypes.object,
  anchorEl: PropTypes.object,
  closePopper: PropTypes.func,
  isEarly: PropTypes.bool,
  isDelayed: PropTypes.bool,
  delayedTime: PropTypes.number,
  isInBucket: PropTypes.bool,
  openDetails: PropTypes.func,
};
